/**
 * WebSocket wire protocol for the convergence commit path.
 *
 * TypeScript mirror of src/lambda/shared/GoobDoobVtt.Domain/ConvergenceMessages.cs.
 * Keep them in lockstep — when one changes, the other has to change too.
 */

import type { ConvergenceDraft, ConvergenceFeature, ConvergenceProfile } from './convergence';

export const ConvergenceClientActions = {
  /** Client→server: commit a ratified merge. */
  Commit: 'convergence/commit',
  /** Client→server: persist the in-progress galaxy draft (owner while unlocked, or DM). */
  DraftSave: 'convergence/draft/save',
  /** Client→server: DM-only — lock/unlock a character's draft. */
  DraftLock: 'convergence/draft/lock',
  /** Client→server: DM-only — unbind (revert) a committed merge. */
  Unbind: 'convergence/unbind',
} as const;

export const ConvergenceServerTypes = {
  /** Server→client: a merge was committed (broadcast to the session). */
  Committed: 'convergence/committed',
  /** Server→client: a draft was saved/updated (broadcast to owner + DM). */
  DraftSaved: 'convergence/draft/saved',
  /** Server→client: a draft's lock state changed (broadcast to owner + DM). */
  DraftLocked: 'convergence/draft/locked',
  /** Server→originator: a draft save/lock was rejected. */
  DraftRejected: 'convergence/draft/rejected',
  /** Server→client: a committed merge was unbound (broadcast to the session). */
  Unbound: 'convergence/unbound',
} as const;

export interface ConvergenceCommitRequest {
  action: typeof ConvergenceClientActions.Commit;
  /** Scopes the audit + broadcast. The CHAR#{id} rows are not session-scoped. */
  sessionId: string;
  profile: ConvergenceProfile;
  features: ConvergenceFeature[];
  reason?: string;
}

export interface ConvergenceCommittedPayload {
  characterId: string;
  name: string;
  level: number;
  version: number;
  featureCount: number;
}

/** DM-only inverse of commit: revert a committed merge and restore the draft. */
export interface ConvergenceUnbindRequest {
  action: typeof ConvergenceClientActions.Unbind;
  sessionId: string;
  characterId: string;
  reason?: string;
}

export interface ConvergenceUnboundPayload {
  characterId: string;
  name: string;
  draftRestored: boolean;
  /** The restored galaxy draft (absent when an existing draft was left untouched). */
  draft?: ConvergenceDraft;
}

export interface ConvergenceDraftSaveRequest {
  action: typeof ConvergenceClientActions.DraftSave;
  sessionId: string;
  draft: ConvergenceDraft;
  /** Optimistic concurrency: the version the client last knew (0 = first save). */
  expectedVersion: number;
  reason?: string;
}

export interface ConvergenceDraftLockRequest {
  action: typeof ConvergenceClientActions.DraftLock;
  sessionId: string;
  characterId: string;
  locked: boolean;
  reason?: string;
}

/** Broadcast on a draft save OR lock change (to owner + DM). Carries the full current draft. */
export interface ConvergenceDraftSavedPayload {
  characterId: string;
  version: number;
  locked: boolean;
  updatedBy?: string;
  updatedAt?: string;
  draft?: ConvergenceDraft;
}

export type ConvergenceDraftRejectReason =
  | 'forbidden'
  | 'locked'
  | 'version_conflict'
  | 'no_draft';

export interface ConvergenceDraftRejectedPayload {
  characterId: string;
  reason: ConvergenceDraftRejectReason;
  version: number;
  /** Server-current draft for the client to reconcile (on version_conflict / locked). */
  draft?: ConvergenceDraft;
}
